import { IScheduledJob } from "@kuika/kuika-cl-model";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { isScheduledJobActive } from "./scheduled-job-row-actions";

/**
 * The landing's "Next run" column, derived entirely from `IScheduledJob.nextRunAt` (computed on
 * the server with the runtime scheduler's own cron and timezone rules) plus the job's own
 * `isActive` flag and timezone. No new backend call.
 *
 * The wall clock is read in the job's stored IANA zone through `Intl`, not through the fixed GMT
 * offset the Timezone column labels it with: most of those zones keep summer time (the default,
 * Europe/London, among them), so a fixed offset would show a 09:00 job as 08:00 half the year.
 * `Intl` with an explicit locale and `hourCycle` also keeps the output identical on every
 * machine, whatever the host's locale or zone.
 */

/** Sortable value for a row with no next-run instant (paused, or none scheduled) — last in an
 *  ascending sort, the convention `timeZoneOffsetMinutes` already uses on this page. */
export const NO_NEXT_RUN_SORT_VALUE: number = Number.MAX_SAFE_INTEGER;

export interface IScheduledJobNextRun {
  /** What the cell shows: "Paused", an em dash, a countdown, or "Tomorrow HH:mm". */
  label: string;
  /** Exact date and time in the job's own timezone, for the cell's title tooltip. Undefined when
   *  there is nothing to show (paused, or no `nextRunAt`). */
  exact?: string;
  /** Sort key: the next-run instant in epoch ms, or `NO_NEXT_RUN_SORT_VALUE`. */
  sortValue: number;
}

interface IWallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const DAY_MS: number = 24 * 60 * 60 * 1000;

const MONTH_ABBREVIATIONS: string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const pad2 = (value: number): string => String(value).padStart(2, "0");

/** The job's stored IANA id, or UTC when there is none or the engine does not know it. */
const resolveTimeZone = (timeZone: string | undefined): string => {
  if (!timeZone) {
    return "UTC";
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
};

const readWallClock = (instantMs: number, timeZone: string): IWallClock => {
  const parts: Intl.DateTimeFormatPart[] = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23"
  }).formatToParts(new Date(instantMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part: Intl.DateTimeFormatPart): boolean => part.type === type)?.value ?? "0");
  return { year: read("year"), month: read("month"), day: read("day"), hour: read("hour"), minute: read("minute") };
};

/** Whole calendar days from one wall-clock date to another, ignoring the time of day. */
const calendarDaysBetween = (from: IWallClock, to: IWallClock): number =>
  Math.round((Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / DAY_MS);

const formatClock = (clock: IWallClock): string => `${pad2(clock.hour)}:${pad2(clock.minute)}`;

const formatExact = (clock: IWallClock): string =>
  `${MONTH_ABBREVIATIONS[clock.month - 1]} ${clock.day}, ${clock.year} ${formatClock(clock)}`;

export const describeScheduledJobNextRun = (job: IScheduledJob, now: number = Date.now()): IScheduledJobNextRun => {
  if (!isScheduledJobActive(job)) {
    return { label: "Paused", sortValue: NO_NEXT_RUN_SORT_VALUE };
  }

  const nextRunAt: string | undefined = job.nextRunAt;
  if (!nextRunAt) {
    return { label: "—", sortValue: NO_NEXT_RUN_SORT_VALUE };
  }

  const targetMs: number = new Date(nextRunAt).getTime();
  const timeZone: string = resolveTimeZone(job.json?.timeZone);
  const target: IWallClock = readWallClock(targetMs, timeZone);

  // Later today reads best as a countdown; tomorrow as the clock time it will show; anything
  // further out falls back to the generic ladder. "Tomorrow" is the job's calendar, not a
  // 24-48h window — a run 30 hours after 02:00 is the day after tomorrow.
  const isTomorrow: boolean = calendarDaysBetween(readWallClock(now, timeZone), target) === 1;
  const label: string = isTomorrow
    ? `Tomorrow ${formatClock(target)}`
    : KMainFunctions.formatRelativeFutureTime(nextRunAt, now);

  return { label, exact: formatExact(target), sortValue: targetMs };
};
